// Availability persistence — bridges the profile 7×24 availability grid to the
// server's formalized AvailabilityRule domain (`/core/availability-rules`,
// WORKSPACE_DATA_MODEL §6.8) via sdk.execute('availabilityRules', …).
// Pure grid ↔ weekly conversions live in availabilityShape.js.
//
// A rule belongs to one Mongo `user`; the server defaults it to the caller on
// create (A2 — a member manages their own availability) and an editor may set
// another member's. Reads are workspace-member gated, writes editor gated —
// a failed write keeps the optimistic local grid and warns, it never throws
// into the UI. workspaceId is resolved server-side from the caller's claims
// (the PartyService contract's claim fallback), so no explicit id is threaded.

import { getSDK } from '@symbo-ls/workspace-shared/functions/sdk.js'
import { getActiveWorkspacePointer } from '@symbo-ls/workspace-shared/functions/activeWorkspacePointer.js'
import { patchPeopleList } from '@symbo-ls/workspace-shared/functions/peopleUtils.js'
import { gridToWeekly, weeklyToGrid } from './availabilityShape.js'

// Active workspace this tab is viewing — the tab's own resolved workspace
// FIRST (root.workspace.id, URL/record-derived — matches loadPrivateData.js
// / pages/main.js's TAB-TRUTH-FIRST precedence), then the per-tab pointer as
// a boot fallback only (before root.workspace has landed). Used to pin
// debounced saves to the workspace they were made in.
//
// Pointer-first was the bug (tickets/workspace.md P1 tenant-isolation audit,
// 2026-08-08): with MWT off, `getActiveWorkspacePointer()` reads SHARED
// `localStorage.activeWorkspace` — another tab's switch could flip what this
// function returns for a tab that already knows its own workspace, corrupting
// the enqueue-vs-fire staleness check below (a legitimate save silently
// dropped, or a real switch missed).
const _calendarActiveWsId = (root) => {
  if (root?.workspace?.id) return root.workspace.id
  try {
    return getActiveWorkspacePointer() || null
  } catch (_) {
    return null
  }
}
// Test-only seam (mirrors AppOrgSwitcher's __orgSwitcherInternals /
// boardPrefs's exported helpers) — not consumed by any component code.
export const __activeWsIdForTest = _calendarActiveWsId

const _patchPerson = (root, userId, patch) => {
  if (!root || !userId) return
  root.update({
    people: patchPeopleList(root.people, userId, patch),
    peopleAll: patchPeopleList(root.peopleAll, userId, patch)
  })
}

// Newest (list is createdAt-desc) non-deleted rule for a member, or null.
export async function fetchAvailabilityRule(userId) {
  if (!userId) return null
  const res = await getSDK().execute('availabilityRules', 'list', { user: userId })
  const rules = Array.isArray(res) ? res : res?.data || []
  return rules[0] || null
}

// Pull a member's server-side availability rule into root.people /
// root.peopleAll — hydrates `availabilityGrid` (from rule.weekly), stashes the
// rule id for updates, and backfills timezone from rule.tz when the person has
// none. Fire-and-forget from profile onCreate; resolves to the rule or null.
export async function hydratePersonAvailability(root, person) {
  const userId = person?.userId
  if (!root || !userId) return null

  const isSelf = person.email && root.currentUserEmail === person.email
  const mongoUserId =
    root.roles?.byUserMongoId?.[userId] ||
    (isSelf && root.currentUserId ? root.currentUserId : userId)

  try {
    const rule = await fetchAvailabilityRule(mongoUserId)
    if (!rule) return null
    const patch = { _availabilityRuleId: rule.id }
    if (Array.isArray(rule.weekly)) {
      patch.availabilityGrid = weeklyToGrid(rule.weekly)
    }
    if (rule.tz && !person.timezone) patch.timezone = rule.tz
    _patchPerson(root, userId, patch)
    return rule
  } catch (e) {
    console.warn('[availability] hydrate failed', e?.message || e)
    return null
  }
}

// Debounced, per-user serialized save of a member's grid. Grid toggles come in
// bursts — one timer per user collapses them, and the per-user promise chain
// guarantees create-then-update ordering (never two creates racing).
const _calendarSaveTimers = {}
const _saveChains = {}

const _persistGrid = async (root, userId, mongoUserId, grid, tz, knownRuleId) => {
  const sdk = getSDK()
  const payload = { weekly: gridToWeekly(grid) }
  if (tz) payload.tz = tz
  // Prefer the rule id CAPTURED at enqueue — after a workspace switch the
  // people lists are wiped/replaced, so a fire-time re-read found no rule
  // and CREATED a duplicate for a member who already had one. Fall back to
  // a fire-time re-read only when none was known at enqueue (an earlier
  // create in this chain may have stashed it after the caller captured
  // `person`).
  const current =
    (root.people || []).find((p) => p.userId === userId) ||
    (root.peopleAll || []).find((p) => p.userId === userId)
  const ruleId = knownRuleId || current?._availabilityRuleId
  if (ruleId) {
    await sdk.execute('availabilityRules', 'update', { id: ruleId, ...payload })
    return
  }
  const created = await sdk.execute('availabilityRules', 'create', {
    user: mongoUserId,
    ...payload
  })
  if (created?.id) _patchPerson(root, userId, { _availabilityRuleId: created.id })
}

export function saveAvailabilityGrid(root, person, grid) {
  const userId = person?.userId
  if (!root || !userId || !Array.isArray(grid)) return
  const tz = person.timezone || null
  const isSelf = person.email && root.currentUserEmail === person.email
  const mongoUserId =
    root.roles?.byUserMongoId?.[userId] ||
    (isSelf && root.currentUserId ? root.currentUserId : userId)

  // Pin the save to the workspace (and rule) it was made in — the 500ms
  // debounce plus the serialized chain can outlive a workspace switch,
  // and availabilityRules writes scope by the ambient claim.
  const ruleIdAtEnqueue = person?._availabilityRuleId || null
  const wsAtEnqueue = _calendarActiveWsId(root)
  clearTimeout(_calendarSaveTimers[userId])
  _calendarSaveTimers[userId] = setTimeout(() => {
    delete _calendarSaveTimers[userId]
    _saveChains[userId] = (_saveChains[userId] || Promise.resolve())
      .then(() => {
        // The workspace moved on while this save was queued — drop it.
        const now = _calendarActiveWsId(root)
        if (wsAtEnqueue && now && now !== wsAtEnqueue) return
        return _persistGrid(root, userId, mongoUserId, grid, tz, ruleIdAtEnqueue)
      })
      .catch((e) => console.warn('[availability] save failed', e?.message || e))
  }, 500)
}
